import React from 'react';

import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  FormControl,
  TableFooter,
  TablePagination,
} from '@mui/material';
import {Create as CreateIcon, Delete as DeleteIcon} from '@mui/icons-material';
import {defineMessages, useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {useQueryClient, useMutation, useQuery} from 'react-query';
import {useForm} from 'react-hook-form';

import API, {NoFlyZonePaginationProps, Types, useCsrfToken} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {
  ConfirmationDialog,
  CircleLoader,
  LoadError,
  SearchForm,
  ValidatableField,
  TooltipButton,
  useGoogleMap,
  useErrorHandler,
} from '@wheeebo/polaris-ui';

const INITIAL_ITEM_COUNT = 25;
const NO_FLY_ZONE_SEARCH_RADIUS_METER = 10000;

const msgs = defineMessages({
  headerTitle: {
    id: 'pages.routeManager.noFlyZone.NoFlyZoneCard.header.title',
    defaultMessage: 'No-fly zone',
  },
  headerSubTitle: {
    id: 'pages.routeManager.noFlyZone.NoFlyZoneCard.header.subTitle',
    defaultMessage: 'List of no-fly zones',
  },
  buttonDelete: {
    id: 'pages.routeManager.noFlyZone.NoFlyZoneCard.button.delete',
    defaultMessage: 'Delete',
  },
  buttonEdit: {
    id: 'pages.routeManager.noFlyZone.NoFlyZoneCard.button.edit',
    defaultMessage: 'Edit',
  },
  cellName: {
    id: 'pages.routeManager.noFlyZone.NoFlyZoneCard.cell.name',
    defaultMessage: 'Name',
  },
  dialogConfirmationTitle: {
    id: 'pages.routeManager.noFlyZone.NoFlyZoneCard.dialog.confirmationTitle',
    defaultMessage: 'Confirm',
  },
  dialogDeleteConfirmationContent: {
    id: 'pages.routeManager.noFlyZone.NoFlyZoneCard.dialog.deleteConfirmationContent',
    defaultMessage: 'Are you really going to delete this group?',
  },
});

interface RowProps {
  noFlyZone: Types.NoFlyZone;
  onDelete: (noFlyZoneId: Types.UUID) => void;
  onSelect: (noFlyZoneId: Types.UUID) => void;
}

function Row({noFlyZone, onDelete, onSelect}: RowProps) {
  const {setCenter} = useGoogleMap();
  const intl = useIntl();
  const location = noFlyZone.area?.at(0)?.at(0);
  const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (noFlyZone.id) {
      onDelete(noFlyZone.id);
    }
  };
  const handleSelect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onSelect(noFlyZone.id);
  };
  const handleCenterChange = React.useCallback(() => {
    if (!location || location.length < 2) {
      return;
    }
    setCenter?.({lat: location[1], lng: location[0]});
  }, [location, setCenter]);

  return (
    <>
      <TableRow hover onClick={handleCenterChange}>
        <TableCell>
          <Typography variant="body2">{noFlyZone.name || noFlyZone.id}</Typography>
        </TableCell>
        <TableCell align="right">
          <TooltipButton
            message={intl.formatMessage(msgs.buttonEdit)}
            icon={<CreateIcon fontSize="small" />}
            onClick={handleSelect}
          />
          <TooltipButton
            message={intl.formatMessage(msgs.buttonDelete)}
            icon={<DeleteIcon fontSize="small" />}
            onClick={handleDelete}
          />
        </TableCell>
      </TableRow>
    </>
  );
}

function NoFlyZoneCard(): JSX.Element {
  const intl = useIntl();
  const {center} = useGoogleMap();
  const {register, formState} = useForm({mode: 'onChange'});
  const {errors} = formState;
  const {csrfToken} = useCsrfToken();
  const navigate = useNavigate();
  const {showAndEmitJsErrorEvent} = useErrorHandler();
  const {showSuccess} = useSnackbar();
  const [page, setPage] = React.useState(0);
  const [name, setName] = React.useState('');
  const queryClient = useQueryClient();
  const [selectedNoFlyZoneId, setSelectedNoFlyZoneId] = React.useState<Types.UUID>();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [query, setQuery] = React.useState<string>();
  const options: NoFlyZonePaginationProps = {
    limit: INITIAL_ITEM_COUNT,
    offset: page * INITIAL_ITEM_COUNT,
    query,
    latitude: center?.lat || 0,
    longitude: center?.lng || 0,
    radius: NO_FLY_ZONE_SEARCH_RADIUS_METER,
  };
  const {data, isError, isLoading} = useQuery(
    center ? API.NoFlyZones.listKey(options) : '',
    () => (center ? API.NoFlyZones.list(options) : undefined),
    {enabled: !!center},
  );
  const {mutate: createNoFlyZone} = useMutation<Types.NoFlyZone, unknown>(
    () => API.NoFlyZones.create(csrfToken, {name}),
    {
      onSuccess: async (noFlyZone: Types.NoFlyZone) => {
        queryClient.invalidateQueries(API.NoFlyZones.CacheKey);
        showSuccess('No-fly zone created');
        navigate(`/noFlyZones/${noFlyZone.id}`);
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const {mutate: deleteNoFlyZone} = useMutation<unknown, unknown>(
    () => {
      setDeleteDialogOpen(false);
      if (!selectedNoFlyZoneId) {
        return Promise.reject('no-fly zone not found');
      }
      return API.NoFlyZones.delete_(csrfToken, selectedNoFlyZoneId);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(API.NoFlyZones.CacheKey);
        showSuccess('No-fly zone delted');
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const handleDelete = async (noFlyZoneId: Types.UUID) => {
    setSelectedNoFlyZoneId(noFlyZoneId);
    setDeleteDialogOpen(true);
  };
  return (
    <Card>
      <CardHeader
        title={intl.formatMessage(msgs.headerTitle)}
        subheader={intl.formatMessage(msgs.headerSubTitle)}
        action={<Button onClick={() => setCreateDialogOpen(true)}>Create New</Button>}
      />
      <CardContent>
        <SearchForm onSearch={setQuery} />
        {isLoading ? <CircleLoader /> : null}
        {isError ? <LoadError /> : null}
        {data && data.total > 0 ? (
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableBody>
                {data?.items?.map((e: Types.NoFlyZone) => (
                  <Row
                    key={e.id}
                    noFlyZone={e}
                    onDelete={handleDelete}
                    onSelect={(noFlyZoneId: Types.UUID) => navigate(`/noFlyZones/${noFlyZoneId}`)}
                  />
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    sx={{
                      '.MuiTablePagination-displayedRows': {m: 1},
                    }}
                    backIconButtonProps={{sx: {p: 0}}}
                    nextIconButtonProps={{sx: {p: 0}}}
                    rowsPerPageOptions={[INITIAL_ITEM_COUNT]}
                    count={data?.total || 0}
                    rowsPerPage={INITIAL_ITEM_COUNT}
                    page={page}
                    SelectProps={{
                      inputProps: {'aria-label': 'rows per page'},
                    }}
                    onPageChange={(_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage)}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2">No record found.</Typography>
        )}
      </CardContent>
      <ConfirmationDialog
        open={createDialogOpen}
        title="Create a new geo fence"
        description="Enter the name of a new geo fence"
        onCancel={() => setCreateDialogOpen(false)}
        onConfirm={() => createNoFlyZone()}>
        <Stack gap={2}>
          <FormControl component="fieldset" fullWidth>
            <ValidatableField
              id="no_fly_zone_name"
              label="No-fly zone name"
              placeholder="Name of a new no-fly zone"
              value={name}
              registerOptions={{
                value: name,
                maxLength: 128,
                required: true,
                onChange: (e) => setName(e.target.value),
              }}
              {...{register, errors}}
              helperTexts={[]}
            />
          </FormControl>
        </Stack>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={intl.formatMessage(msgs.dialogConfirmationTitle)}
        description={intl.formatMessage(msgs.dialogDeleteConfirmationContent)}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={() => deleteNoFlyZone()}
      />
    </Card>
  );
}
export default React.memo(NoFlyZoneCard);
